/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ICardFormProps } from '../types/CardForm';
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {
  Typography,
  AppHeaderBackButton,
  Box,
  Checkbox,
  CheckboxGroup,
  CheckboxTypography,
  Icon,
  Alert,
  AlertTypography,
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  ModalActionButton,
  ModalCloseIconButton,
} from '@ace-clublabs/shared-component-library';
import { getMyAccountURL } from '@ace-clublabs/payments-helper';
import { PaymentType, ErrorCode, IProcessPaymentWithManifestRequest } from '@ace-clublabs/payment-types';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../shared/state';
import PaymentHeader from './PaymentHeader';
import Header from './Header';
import '../styles/override.css';
import { sources } from '../api/sources';
import { IGetAdyenSession, ISubmitAdyenTokenRequest } from '../api/contracts';
import { Pages } from '../shared/pages';
import { navigatePaymentFormBack } from '../utils/paymentNavigationHelper';
import { MANIFEST_TTL_MS } from '../constants';
import { ProcessPaymentHelper } from '../utils/processPaymentHelper';
import UrlHelper from '../utils/urlHelper';
import { ErrorSeverity, ErrorType } from '../shared/types';
import { UtagHelper } from '../utils/utagHelper';
import LogCannon from '@ace-clublabs/log-cannon';
import { PaymentReview } from '../containers/PaymentReview';
import { CardFormValidationHelper } from '../utils/cardFormValidationHelper';
import cvvImage from '../assets/cvv.png';
import { Link, StickyButton } from '@ace-clublabs/shared-component-library';
import { installAdyenMobileClearButtons } from '../utils/installAdyenMobileClearButtons';
import { PaymentMethodsAuthorizationModal } from './PaymentMethodsAuthorizationModal';
import { getTermsUrl } from '../utils/methods';

// Local interfaces for Adyen session data
interface IAdyenEncryptedCardDataLocal {
  encryptedCardNumber: string;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
  encryptedSecurityCode?: string;
  holderName: string;
  postalCode: string;
}

interface IAdyenSessionData {
  sessionId: string;
  encryptedCardData: IAdyenEncryptedCardDataLocal;
}

// Single source of truth — pending product approval.
const UNSUPPORTED_CARD_MESSAGE = 'The card entered is not supported. Please try a different card.';

/** Normalise a raw BIN string to a valid 6- or 8-digit BIN, or '' if unusable.
 *  < 6 → ''
 *  6–7 → first 6
 *  8+  → first 8
 */
export const sanitizeBin = (raw: string): string => {
  if (raw.length < 6) return '';
  if (raw.length >= 8) return raw.substring(0, 8);
  return raw.substring(0, 6);
};

const BILLING_ZIP_LENGTH_ERROR = 'ZIP code must be 5 characters.';

function getBillingZipLengthError(billing: { postalCode?: string; zipCode?: string } | undefined): string | null {
  const code = billing?.postalCode ?? billing?.zipCode;
  if (!code) {
    return null;
  }
  if (code.length !== 5) {
    return BILLING_ZIP_LENGTH_ERROR;
  }
  return null;
}

const CardFormAdyen: React.FC<ICardFormProps> = (props) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setProcessing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCvvModal, setShowCvvModal] = useState(false);
  const [adyenSession, setAdyenSession] = useState<IGetAdyenSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isPaymentReview, setPaymentReview] = useState(false);
  const [, setAdyenCheckoutInstance] = useState<any>(null);
  const [cardComponent, setCardComponent] = useState<any>(null);
  const [adyenSessionData, setAdyenSessionData] = useState<IAdyenSessionData | null>(null);
  const [storePaymentChecked, setStorePaymentChecked] = useState(false);
  const [adyenCardDetails, setAdyenCardDetails] = useState<{
    brand: string;
    last4: string;
    bin: string;
    brandImageUrl: string;
  }>({
    brand: '',
    last4: '',
    bin: '',
    brandImageUrl: '',
  });
  const [showPaymentAuthorization, setShowPaymentAuthorization] = useState(false);
  const [paymentStatusUnknown, setPaymentStatusUnknown] = useState(false);

  // Refs to prevent double initialization with React.StrictMode and to deduplicate Adyen error handling
  const adyenInitializedRef = useRef(false);
  const errorHandledRef = useRef(false);
  const [securityCodeContainerEl, setSecurityCodeContainerEl] = useState<HTMLElement | null>(null);
  const [postalCodeFieldEl, setPostalCodeFieldEl] = useState<HTMLElement | null>(null);
  const [billingZipFieldError, setBillingZipFieldError] = useState<string | null>(null);

  const [_paymentType] = useGlobalState('paymentType') || [];
  const [manifest] = useGlobalState('manifest') || [];
  const [isMobileApp] = useGlobalState('isMobileApp') || [];
  const [paymentMethodsSkipped, setpaymentMethodsSkipped] = useGlobalState('paymentMethodsSkipped') || [];
  const [, setPaymentProcessing] = useGlobalState('paymentProcessing') || [];
  const [, setGlobalError] = useGlobalState('error');
  const [manifestTimestamp] = useGlobalState('manifestTimestamp') || [];

  const {
    manifestId,
    paymentType,
    amount,
    returnUrl,
    autoPay,
    paymentFormSubtitle,
    setParentProcessing,
    isScheduled = false,
    softErrorCode,
    isUnitTest,
    appId,
    scheduledDate,
    tokenOnly,
  } = props;

  const config = manifest?.clientConfig || {};
  const allowStoredPayment = config?.allowAdyenStoredPayment;
  // const isScheduledPayment: boolean = !!isScheduled || !!manifest?.isScheduled;  //TODO not supported yet

  // Determine the payment type to use
  const pmtType =
    paymentType && paymentType !== PaymentType.Undefined
      ? paymentType
      : _paymentType && _paymentType !== PaymentType.Undefined
        ? _paymentType
        : PaymentType.Undefined;

  // Fetch Adyen session on mount
  useEffect(() => {
    let isMounted = true;

    const fetchAdyenSession = async () => {
      try {
        setIsLoadingSession(true);
        const sessionData = await sources.getAdyenSession(manifestId, { paymentType: pmtType });

        if (isMounted) {
          setAdyenSession(sessionData);
        }
      } catch (error) {
        console.error('Failed to fetch Adyen session:', error);
        const errorMessage = 'ERROR: Failed to fetch Adyen session';
        LogCannon.blast(errorMessage, { error, manifestId });
        if (isMounted) {
          setError('Failed to load payment session. Please refresh to try again.');
          setIsLoadingSession(false);
          setParentProcessing({ status: false });
        }
      }
    };

    fetchAdyenSession();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [manifestId, amount]);

  const adyenSessionConfig = useMemo(() => {
    return adyenSession
      ? {
        id: adyenSession.adyen.sessionId,
        sessionData: adyenSession.adyen.sessionData,
      }
      : undefined;
  }, [adyenSession?.adyen.sessionId, adyenSession?.adyen.sessionData]);

  const handleSoftError = useCallback(
    (errorCode: ErrorCode) => {
      if (manifest?.tags) {
        const paymentTags = UtagHelper.tagPage(manifest.tags, 'payment');
        paymentTags['error'] = `blocked payment-${errorCode}`;
        UtagHelper.link(paymentTags);
      }
      switch (errorCode) {
        case ErrorCode.InvalidAccountHolderName:
          setError('Invalid name on the card.');
          break;
        case ErrorCode.InvalidExpirationDate:
          setError('Invalid expiration date.');
          break;
        case ErrorCode.InvalidCard:
        case ErrorCode.InvalidToken:
        case ErrorCode.InvalidInsuranceAutopayPaymentMethod:
        case ErrorCode.CardTypeNotAllowed:
        case ErrorCode.InvalidAccountNumber:
          setError('Invalid card number.');
          break;
        case ErrorCode.StorePaymentError:
          setError('Sorry, your payment method could not be saved for future payments.');
          break;
        case ErrorCode.UnknownCardType:
        case ErrorCode.StandaloneAutoPayError:
        case ErrorCode.InvalidPaymentGatewayToken:
          setError(UNSUPPORTED_CARD_MESSAGE);
          break;
        case ErrorCode.ChargeDeclined:
          setError('Charge declined, please use a different card.');
          break;
        case ErrorCode.CreditCardNotAllowed:
          setError('Only debit cards are accepted, please try again.');
          break;
        case ErrorCode.DebitCardNotAllowed:
          setError('Debit cards are not accepted, please try again.');
          break;
        case ErrorCode.PrepaidCardNotAllowedForAutoPay:
        case ErrorCode.PrepaidCardNotAllowed:
          setError('Prepaid cards are not accepted, please try again.');
          break;
        case ErrorCode.CardMustBeAAAVisa:
          setError('Card must be a AAA Visa card.');
          break;
        case ErrorCode.InvalidZipCode:
          setError('Invalid billing ZIP code.');
          break;
        case ErrorCode.InvalidCVV:
          setError('Invalid CVV/security code.');
          break;
        case ErrorCode.InsufficientFunds:
          setError('Insufficient funds, please try again.');
          break;
        case ErrorCode.NetworkTimeout:
          if (appId === 'manage-saved-payments') {
            setError('Your payment method could not be saved. Please try again.');
          } else {
            setError(null);
            setShowModal(true);
          }
          break;
        default:
          setError(null);
          setShowModal(true);
          break;
      }
    },
    [appId, manifest?.tags]
  );

  useEffect(() => {
    if (softErrorCode) {
      handleSoftError(softErrorCode);
    }
  }, [softErrorCode, handleSoftError]);

  const handlePaymentSuccess = (transactionId: string) => {
    if (setParentProcessing) {
      setParentProcessing({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        willRedirect: true,
        callback: () => {
          const returnUrlWithTransactionId = UrlHelper.addQueryParam(returnUrl, { transactionId });
          window.location.href = returnUrlWithTransactionId;
        },
      });
    }
  };

  const handleTokenOnlySubmit = async () => {
    setProcessing(true);

    if (!adyenSessionData || !adyenSessionData.encryptedCardData) {
      throw {
        type: ErrorSeverity.HARD,
        message: 'Encrypted card data not available for token submission.',
      };
    }

    const tokenZipError = getBillingZipLengthError({
      postalCode: adyenSessionData.encryptedCardData.postalCode,
    });
    if (tokenZipError) {
      setBillingZipFieldError(tokenZipError);
      setParentProcessing({ status: false });
      setProcessing(false);
      return;
    }

    const sessionId = adyenSessionData.sessionId;
    const payload: ISubmitAdyenTokenRequest = {
      adyen: {
        sessionId,
        cardData: {
          last4: adyenCardDetails.last4,
          cardBrand: adyenCardDetails.brand,
          cardHolderName: adyenSessionData.encryptedCardData.holderName,
          zipCode: adyenSessionData.encryptedCardData.postalCode,
          encryptedCardNumber: adyenSessionData.encryptedCardData.encryptedCardNumber,
          encryptedExpiryMonth: adyenSessionData.encryptedCardData.encryptedExpiryMonth,
          encryptedExpiryYear: adyenSessionData.encryptedCardData.encryptedExpiryYear,
          // Only include encryptedSecurityCode if it exists (when CVV is not disabled)
          ...(adyenSessionData.encryptedCardData.encryptedSecurityCode && {
            encryptedSecurityCode: adyenSessionData.encryptedCardData.encryptedSecurityCode,
          }),
          bin: adyenCardDetails.bin || 'credit',
        },
      },
    };

    LogCannon.blast('Submitting Adyen token', { sessionId, payload });

    const response = await sources.submitAdyenToken(sessionId, payload);

    LogCannon.blast('Card Form - Adyen token submission successful', { response });

    setParentProcessing({
      status: true,
      text: '',
      cindy: false,
      displayCheckmark: true,
      willRedirect: true,
      callback: () => {
        window.location.href = returnUrl;
      },
    });
  };

  const handleVerifyAndContinue = async () => {
    setError(null);
    setPaymentStatusUnknown(false);
    setParentProcessing({
      status: true,
      text: 'Verifying your card...',
      cindy: true,
      displayCheckmark: false,
    });
    setProcessing(true);

    try {
      // Validate the card using Adyen
      if (!cardComponent) {
        throw { type: ErrorSeverity.HARD, message: 'Card component not initialized' };
      }

      // Submit card state for validation
      const state = cardComponent.state;
      // ZIP length must run before `state.isValid`: Adyen keeps the form invalid until billing is valid,
      // so we would otherwise always show a generic "Invalid card" and never the ZIP message.
      const billingZipError = getBillingZipLengthError(state.data?.billingAddress);
      if (billingZipError) {
        setParentProcessing({ status: false });
        setProcessing(false);
        setBillingZipFieldError(billingZipError);
        return;
      }

      setBillingZipFieldError(null);

      if (!state.isValid) {
        setParentProcessing({ status: false });
        setProcessing(false);
        handleSoftError(ErrorCode.InvalidCard);
        return;
      }

      // Encrypted card data should already be stored by onChange handler
      // Verify we have the data before proceeding
      if (!adyenSessionData || !adyenSessionData.encryptedCardData) {
        throw {
          type: ErrorSeverity.HARD,
          message: 'Encrypted card data not available. Please try again.',
        };
      }

      if (manifest?.clientConfig?.renderPaymentReview && amount > 0) {
        setParentProcessing({ status: false });
        setProcessing(false);
        setPaymentReview(true);
      } else if (tokenOnly) {
        await handleTokenOnlySubmit();
      } else {
        await submitPaymentFromReview({
          paymentDetails: {
            paymentType: pmtType,
            card: {
              last4: adyenCardDetails.last4,
              brand: adyenCardDetails.brand,
              bin: adyenCardDetails.bin,
            },
          },
          adyenSessionData,
        });
      }
    } catch (error: any) {
      LogCannon.blast('Adyen verification error', error);

      if (error?.type === ErrorSeverity.SOFT) {
        setParentProcessing({
          status: true,
          text: '',
          cindy: false,
          displayCheckmark: true,
          callback: () => {
            setProcessing(false);
          },
        });
        handleSoftError(error.code || ErrorCode.InvalidCard);
      } else {
        errorHandledRef.current = true;
        const errorMessage = 'ERROR: Failed to Verify Card';
        LogCannon.blast('Navigating to Error', {
          appId,
          component: 'CardFormAdyen',
          errorMessage,
          error: typeof error === 'string' ? `${error}` : error,
          manifestId,
        });
        navigate(Pages.Error);
      }
    }
  };

  const submitPaymentFromReview = async (state: any) => {
    const sessionData = state?.adyenSessionData || adyenSessionData;

    if (!sessionData || !sessionData.encryptedCardData) {
      const errorMessage = 'Submit payment from review is missing encrypted card data';

      LogCannon.blast('Navigating to Error', {
        appId,
        component: 'CardFormAdyen',
        errorMessage,
        error: typeof error === 'string' ? `${error}` : error,
        manifestId,
      });
      navigate(Pages.Error);
      return;
    }

    const reviewZipError = getBillingZipLengthError({ postalCode: sessionData.encryptedCardData.postalCode });
    if (reviewZipError) {
      setBillingZipFieldError(reviewZipError);
      if (isPaymentReview) {
        setError(reviewZipError);
      }
      setParentProcessing({ status: false });
      setProcessing(false);
      return;
    }

    // Validate unsupported features
    if (isScheduled && scheduledDate) {
      const errorMessage = 'Scheduled payments not yet implemented for Adyen.';
      LogCannon.blast('Navigating to Error', {
        appId,
        component: 'CardFormAdyen',
        errorMessage,
        error: typeof error === 'string' ? `${error}` : error,
        isScheduled,
        manifestId,
        scheduledDate,
      });
      navigate(Pages.Error);
      return;
    }

    if (tokenOnly) {
      await handleTokenOnlySubmit();
      return;
    }

    setError(null);
    setParentProcessing({
      status: true,
      text: '',
      cindy: false,
      displayCheckmark: false,
    });
    setProcessing(true);

    try {
      const isManifestStale = !!manifestTimestamp && Date.now() - manifestTimestamp > MANIFEST_TTL_MS;
      if (isManifestStale) {
        throw { type: ErrorType.SESSION_TIMEOUT, message: 'Manifest is stale' };
      }
      setPaymentProcessing(true);
      const processAdyenPaymentBody: IProcessPaymentWithManifestRequest = {
        manifestId: manifestId,
        paymentType: pmtType === PaymentType.Undefined ? PaymentType.CreditOrDebit : pmtType,
        adyenData: {
          sessionId: sessionData.sessionId, // For logging only
          cardData: {
            cardBrand: adyenCardDetails.brand,
            bin: adyenCardDetails.bin || 'credit',
            last4: adyenCardDetails.last4,
            cardHolderName: sessionData.encryptedCardData.holderName,
            zipCode: sessionData.encryptedCardData.postalCode,
            encryptedCardNumber: sessionData.encryptedCardData.encryptedCardNumber,
            encryptedExpiryMonth: sessionData.encryptedCardData.encryptedExpiryMonth,
            encryptedExpiryYear: sessionData.encryptedCardData.encryptedExpiryYear,
            // Only include encryptedSecurityCode if it exists (when CVV is not disabled)
            ...(sessionData.encryptedCardData.encryptedSecurityCode && {
              encryptedSecurityCode: sessionData.encryptedCardData.encryptedSecurityCode,
            }),
          },
        },
        scheduledDate: scheduledDate,
      };

      if ((allowStoredPayment && storePaymentChecked) || appId === 'manage-saved-payments') {
        processAdyenPaymentBody.storePayment = { enable: true };
      }

      LogCannon.blast('Processing Adyen payment from review', processAdyenPaymentBody);

      const { transactionId } = await ProcessPaymentHelper.processPayment(processAdyenPaymentBody);

      handlePaymentSuccess(transactionId);
    } catch (error: any) {
      LogCannon.blast('Adyen payment error', error);

      if (error?.type === ErrorSeverity.SOFT && error?.message === 'payment_status_unknown') {
        setParentProcessing({
          status: true,
          text: '',
          cindy: false,
          displayCheckmark: true,
          callback: () => {
            setProcessing(false);
            setPaymentReview(false);
          },
        });
        LogCannon.blast('Payment status unknown after Adyen card payment — showing inline message', {
          appId,
          component: 'CardFormAdyen',
          manifestId,
        });
        setPaymentStatusUnknown(true);
      } else if (error.type === ErrorSeverity.SOFT) {
        setParentProcessing({
          status: true,
          text: '',
          cindy: false,
          displayCheckmark: true,
          callback: () => {
            setProcessing(false);
            setPaymentReview(false);
          },
        });
        handleSoftError(error.code || ErrorCode.UnhandledError);
      } else {
        const isExpired = error?.type === ErrorType.SESSION_TIMEOUT;
        let errorMessage = 'ERROR: Failed to Process Adyen Payment From Review';

        if (error.code === ErrorCode.TooManyRequests) {
          setGlobalError({ errorType: ErrorType.RATE_LIMITED, errorMessage: '' });
          errorMessage = 'ERROR: Failed to Process Adyen Payment From Review Rate Limited';
        } else if (isExpired) {
          setGlobalError({ errorType: ErrorType.SESSION_TIMEOUT, errorMessage: error?.message ?? '' });
        }

        LogCannon.blast(isExpired ? 'Navigating to Session Error' : 'Navigating to Error', {
          appId,
          component: 'CardFormAdyen',
          errorMessage,
          error: typeof error === 'string' ? `${error}` : error,
          manifestId,
          ...(isExpired && { manifestExpiredAfterMs: Date.now() - manifestTimestamp!, expiredManifestId: manifestId }),
        });
        navigate(Pages.Error);
      }
    }
  };

  useEffect(() => {
    // Reset initialization ref when session config changes
    adyenInitializedRef.current = false;
    errorHandledRef.current = false;
  }, [adyenSessionConfig]);

  useEffect(() => {
    if (!adyenSessionConfig || !adyenSession) {
      return;
    }

    // Prevent double initialization with React.StrictMode in development
    if (adyenInitializedRef.current) {
      return;
    }
    adyenInitializedRef.current = true;

    let isMounted = true;
    let card: any = null;

    const initializeAdyen = async () => {
      try {
        if (!softErrorCode) {
          setError(null);
        }

        if (!isMounted) {
          return;
        }

        if (!adyenSession.adyen.clientKey) {
          throw new Error('Adyen clientKey is required but was not provided');
        }

        if (!adyenSession.adyen.sessionId || !adyenSession.adyen.sessionData) {
          throw new Error('Adyen sessionId or sessionData is missing from adyenSession');
        }

        let loadingHidden = false;

        const hideLoading = () => {
          if (!loadingHidden && isMounted) {
            loadingHidden = true;

            setTimeout(() => {
              if (isMounted) {
                setIsLoadingSession(false);
                setParentProcessing({ status: false });
              }
            }, 1000);
          }
        };
        const translations = {
          'creditCard.holderName': 'Name on the card',
          'creditCard.expiryDate.label': 'Expiration date',
          'creditCard.expiryDate.contextualText': 'MM/YY',
          'creditCard.securityCode.label': 'CVV/security code',
          'cc.num.900': 'Card number is required.',
          'cc.num.901': 'Card number is not valid.',
          'cc.num.902': 'Card number is not valid.',
          'cc.num.903': UNSUPPORTED_CARD_MESSAGE,
          'cc.dat.912': 'Card expiration is not valid.',
          'cc.dat.911': 'Card expiration is not valid.',
          zipCode: 'Billing ZIP code',
          postalCode: 'Billing ZIP code',
          payButton: 'Continue',
          saveDetails: 'Continue',
          confirmPreauthorization: 'Continue',
        };
        const globalConfiguration = {
          session: adyenSessionConfig,
          environment: adyenSession.adyen.environment, // From backend API (test or live)
          amount: {
            value: props.amount,
            currency: 'USD',
          },
          locale: 'en-US',
          countryCode: 'US',
          clientKey: adyenSession?.adyen.clientKey,

          showPayButton: false, // We'll use our custom Continue button instead
          translations: {
            'en-US': {
              ...translations,
            },
            // Some Adyen Web setups use underscore locales (e.g. en_US). Keep both to be safe.
            en_US: {
              ...translations,
            },
            // Some environments/tools may pass a language-only locale (e.g. "en").
            en: {
              ...translations,
            },
          },

          onError: (error: any, component: any) => {
            setTimeout(() => {
              if (!isMounted) return;
              if (errorHandledRef.current) {
                LogCannon.blast('Adyen onError suppressed — already handled by catch block', {
                  adyenComponent: component,
                  appId,
                  component: 'CardFormAdyen',
                  error: typeof error === 'string' ? `${error}` : error,
                  manifestId,
                });
                return;
              }
              const errorMessage = 'ERROR: Adyen Error';
              setParentProcessing({ status: false });
              setProcessing(false);
              LogCannon.blast('Navigating to Error', {
                adyenComponent: component,
                appId,
                component: 'CardFormAdyen',
                errorMessage,
                error: typeof error === 'string' ? `${error}` : error,
                manifestId,
              });
              navigate(Pages.Error);
            }, 0);
          },
        };

        const cardConfiguration = {
          billingAddressRequired: true,
          billingAddressMode: 'partial' as const,
          billingAddressAllowedCountries: ['US'],
          hasHolderName: true,
          holderNameRequired: true,
          positionHolderNameOnTop: true,
          autoFocus: false,
          maskSecurityCode: true,
          showPayButton: false, // We'll use our custom Continue button instead
          // Hide CVV field when disableCVV is true
          hideCVC: !!config.disableCVV,
          amount: {
            value: 0,
            currency: 'USD',
          },
          onFieldValid: (event: any) => {
            // Only handle card number field - ignore other fields (CVV, expiry, etc.)
            if (event?.fieldType === 'encryptedCardNumber') {
              if (event.valid && event.endDigits) {
                // Capture endDigits from the card number field when valid
                setAdyenCardDetails((prev) => ({
                  ...prev,
                  last4: event.endDigits,
                }));
              } else if (!event.valid) {
                // Reset last4 when card number field becomes invalid
                setAdyenCardDetails((prev) => ({
                  ...prev,
                  last4: '',
                }));
              }

              // Handle bin from issuerBin (16+ digit cards only; not available for Amex)
              const fullBin = event.issuerBin ? event.issuerBin.toString() : '';

              if (event.valid && fullBin.length >= 6) {
                // Card complete and validated — upgrade to most precise BIN available
                const bin = sanitizeBin(fullBin);
                setAdyenCardDetails((prev) => {
                  return { ...prev, bin };
                });
              } else if (!event.valid) {
                // Card became invalid — clear the BIN
                setAdyenCardDetails((prev) => {
                  return { ...prev, bin: '' };
                });
                setError(null);
              } else {
                // Valid but no issuerBin (e.g. Amex 15-digit cards) — preserve captured BIN
                setAdyenCardDetails((prev) => {
                  return prev;
                });
              }
            }
          },
          onBinValue: (event: { binValue: string }) => {
            const candidate = event?.binValue || '';
            const newBin = sanitizeBin(candidate);
            if (!newBin) {
              // User is still typing (or deleting back) — clear the BIN
              setAdyenCardDetails((prev) => {
                return { ...prev, bin: '' };
              });
            } else {
              setAdyenCardDetails((prev) => {
                // If we already have an 8-digit confirmed BIN and the first 6 digits match,
                // the user is still on the same card — preserve the higher-precision 8-digit BIN.
                if (prev.bin.length === 8 && prev.bin.substring(0, 6) === newBin.substring(0, 6)) {
                  return prev;
                }
                // Otherwise take the sanitized BIN (new card or first time)
                return { ...prev, bin: newBin };
              });
            }
          },
          onBinLookup: (binLookupData: any) => {
            // Capture and map the card brand from supportedBrands
            const supportedBrands = binLookupData?.supportedBrands || [];
            if (supportedBrands.length > 0) {
              const adyenBrand = supportedBrands[0]; // Use the first supported brand
              const mappedBrand = CardFormValidationHelper.mapAdyenBrandToStandard(adyenBrand);

              // Update card details with mapped brand (preserve last4 and bin as-is)
              setAdyenCardDetails((prev) => {
                return {
                  brand: mappedBrand,
                  last4: prev.last4, // Keep existing last4 (empty or populated)
                  bin: prev.bin, // Keep existing bin (empty or populated)
                  brandImageUrl: prev.brandImageUrl,
                };
              });
            }
          },
          onChange: (state: any) => {
            hideLoading();

            const billingZipError = getBillingZipLengthError(state.data?.billingAddress);
            // Inline ZIP length message: set on blur only (see billing ZIP useEffect). Clear as soon as length is valid.
            if (!billingZipError) {
              setBillingZipFieldError(null);
              setError((prev) => (prev === BILLING_ZIP_LENGTH_ERROR ? null : prev));
            }
            setIsFormValid(!!state.isValid && !billingZipError);

            // When form is valid, capture and store encrypted card data for payment submission
            if (state.isValid && state.data?.paymentMethod) {
              const paymentMethod = state.data.paymentMethod;
              const billingAddress = state.data.billingAddress || {};

              // Only store if we have encrypted data
              if (paymentMethod.encryptedCardNumber) {
                const encryptedCardData: IAdyenEncryptedCardDataLocal = {
                  encryptedCardNumber: paymentMethod.encryptedCardNumber,
                  encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
                  encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
                  holderName: paymentMethod.holderName || '',
                  postalCode: billingAddress.postalCode || '',
                };

                // Only include encryptedSecurityCode if it's present
                // When hideCVC is true, Adyen SDK won't provide encryptedSecurityCode
                if (paymentMethod.encryptedSecurityCode) {
                  encryptedCardData.encryptedSecurityCode = paymentMethod.encryptedSecurityCode;
                }

                setAdyenSessionData({
                  sessionId: adyenSessionConfig?.id || '',
                  encryptedCardData,
                });
              }
            }

            // Extract card brand and potentially last4 from the state
            if (state.data?.cardDetails) {
              setAdyenCardDetails((prev) => ({
                brand: state.data.cardDetails.type || prev.brand,
                last4: prev.last4, // Preserve last4 captured from onFieldValid or onBinValue
                bin: prev.bin, // Preserve bin captured from onBinValue
                brandImageUrl: prev.brandImageUrl,
              }));
            }
            // Update button state on change - disable if form invalid
            setTimeout(() => {
              const payButton = document.querySelector('.adyen-checkout__button') as HTMLButtonElement;
              if (payButton) {
                // If isUnitTest is true, don't disable the button (for testing purposes)
                const zipInvalid = !!getBillingZipLengthError(state.data?.billingAddress);
                const shouldDisable = !state.isValid || zipInvalid;
                payButton.disabled = isUnitTest ? false : shouldDisable;
                if (shouldDisable && !isUnitTest) {
                  payButton.setAttribute('data-form-invalid', 'true');
                } else {
                  payButton.removeAttribute('data-form-invalid');
                }
              }
            }, 0);
          },
          brands: config.adyenCardBrands ?? ['amex', 'diners', 'discover', 'jcb', 'mc', 'visa'],
          styles: {
            base: {
              color: '#3D4043',
              fontSize: '16px',
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: '400',
            },
            error: {
              color: '#3D4043',
            },
          },
        };

        const checkout = await AdyenCheckout(globalConfiguration);

        // Check again if component is still mounted before mounting card
        if (!isMounted) {
          return;
        }

        card = new Card(checkout, cardConfiguration).mount('#card-container');

        // Store checkout and card instances for later use
        if (isMounted) {
          setAdyenCheckoutInstance(checkout);
          setCardComponent(card);
        }

        // Dismiss parent loading without waiting for the first onChange (Adyen may not emit until user input).
        hideLoading();

        // Note: No need to manage Adyen pay button since we're using custom Continue button
      } catch (error) {
        const errorMessage = 'ERROR: Failed to initialize Adyen card form';

        if (isMounted) {
          errorHandledRef.current = true;
          setParentProcessing?.({ status: false });
          LogCannon.blast('Navigating to Error', {
            appId,
            component: 'CardFormAdyen',
            errorMessage,
            error: typeof error === 'string' ? `${error}` : error,
            manifestId,
          });
          navigate(Pages.Error);
        }
      }
    };

    initializeAdyen();

    // Cleanup function to prevent memory leaks and duplicate mounts
    return () => {
      isMounted = false;
      if (card) {
        try {
          card.unmount?.();
        } catch (e) {
          console.warn('Error unmounting Adyen card:', e);
        }
      }
    };
  }, [adyenSessionConfig, adyenSession, config.disableCVV]);

  const handleBack = () => {
    navigatePaymentFormBack(navigate, manifest, {
      softErrorCode: props.softErrorCode,
      paymentMethodsSkipped,
      onClearPaymentMethodsSkipped: () => setpaymentMethodsSkipped(false),
    });
  };

  const cvvModalHandler = (e: any) => {
    e?.preventDefault?.();
    setShowCvvModal(true);
  };

  useEffect(() => {
    if (!cardComponent) return;

    const onInputCapture = (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target?.closest('.adyen-checkout__card__holderName')) return;
      const cleaned = target.value.replace(/[^a-zA-Z\s.'‘’\-]/g, '').replace(/\s+/g, ' ').slice(0, 26);
      if (cleaned !== target.value) {
        target.value = cleaned;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('.adyen-checkout__card__holderName')) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return;
      const allowed = /[a-zA-Z\s.'‘’\-]/.test(e.key);
      if (!allowed) e.preventDefault();
    };

    document.addEventListener('input', onInputCapture, true);
    document.addEventListener('keydown', onKeyDown, true);

    const root = document.getElementById('card-container') ?? document.body;
    const existing = root.querySelector('.adyen-checkout__card__holderName input') as HTMLInputElement | null;
    if (existing) {
      existing.maxLength = 26;
    } else {
      const observer = new MutationObserver(() => {
        const holderInput = root.querySelector('.adyen-checkout__card__holderName input') as HTMLInputElement | null;
        if (!holderInput) return;
        observer.disconnect();
        holderInput.maxLength = 26;
      });
      observer.observe(root, { childList: true, subtree: true });
      return () => {
        observer.disconnect();
        document.removeEventListener('input', onInputCapture, true);
        document.removeEventListener('keydown', onKeyDown, true);
      };
    }

    return () => {
      document.removeEventListener('input', onInputCapture, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [cardComponent]);

  // Billing US ZIP: digits only (keydown + input/paste), max 5 — matches getBillingZipLengthError.
  useEffect(() => {
    if (!cardComponent) return;

    let isUnmounted = false;
    const timeouts: number[] = [];

    const attachWhenZipInputReady = () => {
      if (isUnmounted) return;

      const root = document.getElementById('card-container');
      const field = root?.querySelector(
        '.adyen-checkout__field--postalCode, .adyen-checkout__field--zipCode'
      ) as HTMLElement | null;
      const zipInput = field?.querySelector('input') as HTMLInputElement | null;
      if (!zipInput) {
        timeouts.push(window.setTimeout(attachWhenZipInputReady, 50));
        return;
      }

      try {
        zipInput.inputMode = 'numeric';
        zipInput.maxLength = 5;
      } catch {
        /* no-op: readonly or strict iframe edge cases */
      }

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key.length !== 1) return;
        if (/\d/.test(e.key)) return;
        e.preventDefault();
      };

      const onInputCapture = (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        if (!target) return;
        const onlyDigits = target.value.replace(/\D/g, '').slice(0, 5);
        if (onlyDigits !== target.value) {
          target.value = onlyDigits;
        }
      };

      const onZipBlur = () => {
        setBillingZipFieldError(getBillingZipLengthError({ postalCode: zipInput.value }));
      };

      zipInput.addEventListener('keydown', onKeyDown);
      zipInput.addEventListener('input', onInputCapture, true);
      zipInput.addEventListener('blur', onZipBlur);

      return () => {
        zipInput.removeEventListener('keydown', onKeyDown);
        zipInput.removeEventListener('input', onInputCapture, true);
        zipInput.removeEventListener('blur', onZipBlur);
      };
    };

    const cleanup = attachWhenZipInputReady();

    return () => {
      isUnmounted = true;
      timeouts.forEach((t) => clearTimeout(t));
      if (typeof cleanup === 'function') cleanup();
    };
  }, [cardComponent]);

  // Capture card brand image URL from Adyen's rendered icon
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;

    const captureImageUrl = () => {
      if (cancelled) return;

      const brandWrapper = document.querySelector(
        '.adyen-checkout__card__form .adyen-checkout__field--cardNumber .adyen-checkout-brand-wrapper'
      );
      if (!brandWrapper) {
        setTimeout(captureImageUrl, 100);
        return;
      }

      const brandImage = brandWrapper.querySelector('img.adyen-checkout__card__cardNumber__brandIcon');
      if (brandImage instanceof HTMLImageElement && brandImage.src) {
        setAdyenCardDetails((prev) => ({
          ...prev,
          brandImageUrl: brandImage.src,
        }));
      }
    };

    // Watch for image changes
    const intervalId = setInterval(() => {
      if (cancelled) return;

      const brandWrapper = document.querySelector(
        '.adyen-checkout__card__form .adyen-checkout__field--cardNumber .adyen-checkout-brand-wrapper'
      );
      if (brandWrapper) {
        const brandImage = brandWrapper.querySelector('img.adyen-checkout__card__cardNumber__brandIcon');
        if (brandImage instanceof HTMLImageElement && brandImage.src) {
          setAdyenCardDetails((prev) => {
            if (prev.brandImageUrl !== brandImage.src) {
              return { ...prev, brandImageUrl: brandImage.src };
            }
            return prev;
          });
        }
      }
    }, 500);

    captureImageUrl();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [cardComponent]);

  // Mount the CVV "Where do I find this?" link directly under the Adyen CVV field
  // by portaling into the security code field container once it exists in the DOM.
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector('.adyen-checkout__field--securityCode') as HTMLElement | null;
      if (el) {
        setSecurityCodeContainerEl(el);
        return;
      }
      // Adyen renders async; retry shortly.
      setTimeout(tryFind, 50);
    };

    tryFind();
    return () => {
      cancelled = true;
      setSecurityCodeContainerEl(null);
    };
  }, [cardComponent]);

  // Resolve billing ZIP field container for inline error (portaled, matches other Adyen field errors).
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const root = document.getElementById('card-container');
      const el = root?.querySelector(
        '.adyen-checkout__field--postalCode, .adyen-checkout__field--zipCode'
      ) as HTMLElement | null;
      if (el) {
        setPostalCodeFieldEl(el);
        return;
      }
      setTimeout(tryFind, 50);
    };

    tryFind();
    return () => {
      cancelled = true;
      setPostalCodeFieldEl(null);
    };
  }, [cardComponent]);

  useEffect(() => {
    if (!postalCodeFieldEl) return;
    if (billingZipFieldError) {
      postalCodeFieldEl.classList.add('fp-billing-zip-error');
    } else {
      postalCodeFieldEl.classList.remove('fp-billing-zip-error');
    }
  }, [postalCodeFieldEl, billingZipFieldError]);

  useEffect(() => {
    if (!cardComponent) return;
    return installAdyenMobileClearButtons({
      containerId: 'card-container',
      cardComponent,
    });
  }, [cardComponent]);

  const modalDialog = (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      variants={{
        sm: 'bottom-sheet',
        md: 'regular',
      }}
    >
      <ModalTitle id={`error-modal-title`} sx={{ marginRight: '2rem' }}>
        Please try again.
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowModal(false)} />
      </ModalTitle>

      <ModalContent id="error-modal-content">
        <Typography variant="b1Regular">There was an unexpected error.</Typography>
      </ModalContent>
      <ModalActions
        actionLayout={{
          xs: 'fullWidth',
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton label="OK" onClick={() => setShowModal(false)} />
      </ModalActions>
    </Modal>
  );

  const cvvModal = (
    <Modal
      open={showCvvModal}
      onClose={() => setShowCvvModal(false)}
      variants={{
        sm: 'bottom-sheet',
        md: 'regular',
      }}
    >
      <ModalTitle id={`modal-title-cvv`} sx={{ marginRight: '2rem' }}>
        Where do I find this?
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowCvvModal(false)} />
      </ModalTitle>

      <ModalContent id="modal-content-cvv">
        <Typography variant="b1Regular">
          The CVV or Card Verification Value is a 3-digit code usually found on the back of Visa or Mastercard credit
          and debit cards. For American Express cards, it is a 4-digit code on the front of your card.
        </Typography>
        <div className="cvv-img-div">
          <img src={cvvImage} className="cvv-img" alt="cvv" />
        </div>
      </ModalContent>
      <ModalActions
        actionLayout={{
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton label="Got it" onClick={() => setShowCvvModal(false)} />
      </ModalActions>
    </Modal>
  );

  const amountDue = amount > 0;
  const renderPaymentReview = manifest?.clientConfig?.renderPaymentReview;
  const buttonText = !isScheduled && amountDue && !renderPaymentReview ? 'Pay now' : 'Continue';
  const disabled = isUnitTest ? false : !isFormValid;

  const isMap = appId === 'manage-autopay';
  const mapChangeFlow = isMap && manifest?.renderTsAndCs?.render;
  const mapNewEnrollFlow = isMap && !manifest?.renderTsAndCs?.render;
  const renderTermsAndPaymentAuth = mapChangeFlow && paymentMethodsSkipped;
  const willVisitPaymentReview = renderPaymentReview && amount > 0;
  const renderPaymentAuth =
    !renderTermsAndPaymentAuth && ((mapNewEnrollFlow && amount === 0) || mapChangeFlow || !willVisitPaymentReview); //TODO: || isStorePaymentFlow));
  const renderNoChargeAlert = config?.renderNoChargeAlert && amount === 0;

  return (
    <>
      {/* Debug Info */}
      <div style={{ padding: '10px', backgroundColor: '#f0f0f0', margin: '10px', fontSize: '12px', display: 'none' }}>
        isLoading: {String(isLoadingSession)} | hasSession: {String(!!adyenSession)} | hasConfig:{' '}
        {String(!!adyenSessionConfig)} | initialized: {String(adyenInitializedRef.current)}
      </div>
      <div
        style={isLoadingSession || isProcessing ? { visibility: 'hidden', position: 'absolute', left: '-9999px' } : {}}
      >
        {isPaymentReview && (
          <PaymentReview
            amount={manifest?.amount!}
            paymentDetails={{
              paymentType: PaymentType.Undefined,
              card: {
                last4: adyenCardDetails.last4 || '',
                brand: adyenCardDetails.brand || '',
                bin: adyenCardDetails.bin || 'credit',
                brandImageUrl: adyenCardDetails.brandImageUrl || '',
              },
            }}
            adyenSessionData={adyenSessionData || undefined}
            pit={manifest?.pit ? { ...manifest.pit } : undefined}
            scheduledDate={scheduledDate}
            onSubmit={submitPaymentFromReview}
            onBack={() => {
              setPaymentReview(false);
              setParentProcessing({ status: false });
            }}
          />
        )}

        <Box className={`payment-form ${!isPaymentReview && !isProcessing ? '' : 'shrink'}`} tabIndex={0}>
          <Box className="payment-form-cream-section">
            <Box className="payment-form-body inner-container">
              <Header />
              {!isMobileApp && <AppHeaderBackButton id="back-button" onClick={handleBack} />}
              <Box className="secure-checkout">
                <Icon iconName="lock-keyhole-solid" className="lock-icon" />
                <span>Secure checkout</span>
              </Box>
              <PaymentHeader paymentType={pmtType} amount={amount} autoPay={autoPay} />
              {paymentFormSubtitle ? (
                <Typography variant="b1Regular" data-quid="card-form-subtitle">
                  {paymentFormSubtitle}
                </Typography>
              ) : (
                <Box id="card-form-subtitle-container">
                  {config?.allowedPaymentTypes?.includes(PaymentType.DebitOnly) && (
                    <Box className="card-subtitle" id="payment-method-debit-card">
                      <Typography variant="b1Regular" data-quid="card-form-subtitle">
                        Debit card
                      </Typography>
                      <Box className="card-logo" id="debit-card-logos">
                        <Icon iconName="mastercard-logo" className="card-header-icons" />
                        <Icon iconName="visa-logo" className="card-header-icons" />
                      </Box>
                    </Box>
                  )}
                  {config?.allowedPaymentTypes?.includes(PaymentType.CreditOrDebit) && (
                    <Box
                      className="card-subtitle"
                      id="payment-method-credit-card"
                      sx={{ alignContent: 'space-between' }}
                    >
                      <Typography
                        variant="b1Regular"
                        data-quid="card-form-subtitle"
                        sx={{ color: '#3D4043 !important' }}
                      >
                        Credit card
                      </Typography>
                      <Box className="card-logo" id="credit-card-logos">
                        <Icon iconName="amex-logo" className="card-header-icons" />
                        <Icon iconName="mastercard-logo" className="card-header-icons" />
                        <Icon iconName="visa-logo" className="card-header-icons" />
                        <Icon iconName="discover-logo" className="card-header-icons" />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box className="payment-form-body card-form inner-container">
            {paymentStatusUnknown  && (
              <Alert severity="warning" id="payment-status-unknown-alert" displayCloseButton={false}>
                <AlertTypography>
                  Payment submitted but there was an issue confirming it — please check{' '}
                  <Link href={getMyAccountURL()} target="_blank" rel="noopener noreferrer">
                    My Account
                  </Link>{' '}
                  before retrying to avoid a duplicate charge.
                </AlertTypography>
              </Alert>
            )}
            {error && (
              <Alert severity="error" id="soft-error-alert" displayCloseButton={false}>
                <AlertTypography>{error}</AlertTypography>
              </Alert>
            )}
            <div id="card-container"></div>
            {postalCodeFieldEl &&
              billingZipFieldError &&
              !isPaymentReview &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-billing-zip-error="true"
                >
                  {billingZipFieldError}
                </div>,
                postalCodeFieldEl
              )}
            {securityCodeContainerEl &&
              createPortal(
                <div className="cvv-help-link-wrap" style={{ display: 'block' }}>
                  <span style={{ display: 'inline-block' }}>
                    <Link component="button" id="cvv-desc-link" className="secondary" onClick={cvvModalHandler}>
                      Where do I find this?
                    </Link>
                  </span>
                </div>,
                securityCodeContainerEl
              )}
            {allowStoredPayment && (
              <CheckboxGroup className="save-this-payment" variant="text">
                <Checkbox
                  name="store-payment"
                  checked={storePaymentChecked}
                  label={<CheckboxTypography>Save for future payments</CheckboxTypography>}
                  enableForm={false}
                  onClick={() => setStorePaymentChecked((prev) => !prev)}
                />
              </CheckboxGroup>
            )}
            {renderPaymentAuth && (
              <Typography variant="b2Regular" id="payment-authorization-text">
                By clicking {buttonText}, I hereby state that I am an authorized user for the above payment method, and
                have read and agree to the{' '}
                <Link
                  id="payment-authorization-link"
                  component="button"
                  sx={{ verticalAlign: 'baseline' }}
                  onClick={() => {
                    setShowPaymentAuthorization(true);
                  }}
                  variant="b2Regular"
                >
                  Payment Authorization
                </Link>
                .
              </Typography>
            )}
            {renderTermsAndPaymentAuth && (
              <Typography variant="b2Regular" id="payment-authorization-and-terms-text">
                By clicking Continue, I hereby enroll in Auto Pay and that I am an authorized user for the above payment
                method I have selected, and have read and agree to the{' '}
                <Link
                  data-id="terms-conditions-link"
                  id="terms-conditions-link"
                  variant="b2Regular"
                  href={getTermsUrl()}
                  target="_blank"
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Terms & Conditions
                </Link>{' '}
                <Typography variant="b2Regular" sx={{ display: 'inline-block' }}>
                  and{' '}
                </Typography>
                <Link
                  id="payment-authorization-link"
                  component="button"
                  sx={{ verticalAlign: 'baseline' }}
                  onClick={() => {
                    setShowPaymentAuthorization(true);
                  }}
                  variant="b2Regular"
                >
                  Payment Authorization
                </Link>
                .
              </Typography>
            )}
          </Box>

          {showPaymentAuthorization && (
            <PaymentMethodsAuthorizationModal
              handleClose={() => {
                setShowPaymentAuthorization(false);
              }}
              ctaText={buttonText}
            ></PaymentMethodsAuthorizationModal>
          )}

          <StickyButton
            id="cc-submit"
            slotProps={{
              primaryButton: {
                label: buttonText,
                onClick: handleVerifyAndContinue,
                disabled,
                endIcon: !renderNoChargeAlert && amount > 0 ? <Icon iconName="lock-keyhole-solid" /> : undefined,
              },
            }}
            renderAmount={renderNoChargeAlert || amount > 0 ? amount : undefined}
          />
        </Box>
        {modalDialog}
        {cvvModal}
      </div>
    </>
  );
};

export default CardFormAdyen;
